/**
 * AdminBackupCentre — One-click CSV backup for the School module.
 *
 * Modules covered: Exam marks · Daily attendance · Library borrows · Fee payments.
 * Filters: tenant_id · academic year (exams) · class · status (library) · term (fees)
 *          + universal date range.
 *
 * Routes to: /admin/backup
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  GraduationCap, Calendar, BookOpen, Receipt, Download,
  Database, RefreshCw,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';
import SchoolBackupSchedulePanel from '../components/SchoolBackupSchedulePanel';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

// ---------------------------------------------------------------------------
// Download helper — fetch the CSV with auth headers, then trigger a browser
// download from the resulting blob. We can't use a plain <a href=...> because
// the API requires the Bearer token.
// ---------------------------------------------------------------------------
const downloadCsv = async (url, fallbackFilename) => {
  const res = await axios.get(url, { headers: authH(), responseType: 'blob' });
  // Try to read the filename from Content-Disposition
  const cd = res.headers['content-disposition'] || '';
  const m = cd.match(/filename="([^"]+)"/);
  const filename = m ? m[1] : fallbackFilename;
  const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
};

const buildQuery = (params) => {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v) qs.set(k, v);
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
};

const TILE_ACCENTS = {
  indigo: 'from-indigo-500/15 to-indigo-500/5 text-indigo-300 border-indigo-400/30',
  emerald: 'from-emerald-500/15 to-emerald-500/5 text-emerald-300 border-emerald-400/30',
  amber: 'from-amber-500/15 to-amber-500/5 text-amber-300 border-amber-400/30',
  sky: 'from-sky-500/15 to-sky-500/5 text-sky-300 border-sky-400/30',
};

const ExportTile = ({ icon: Icon, accent, title, description, count, endpoint,
                       fallbackName, extraFilters, sharedFilters, testId }) => {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try {
      const params = { ...sharedFilters, ...extraFilters };
      await downloadCsv(`${API}${endpoint}${buildQuery(params)}`, fallbackName);
      toast.success(`${title} CSV downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Download failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="bg-slate-900/60 border-slate-800" data-testid={testId}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className={`rounded-xl bg-gradient-to-br ${TILE_ACCENTS[accent]} border p-3 inline-flex`}>
            <Icon className="h-4 w-4" />
          </div>
          {typeof count === 'number' && (
            <Badge variant="outline" className="text-[11px] text-slate-200 border-slate-600">
              {count.toLocaleString()} {count === 1 ? 'row' : 'rows'}
            </Badge>
          )}
        </div>
        <div>
          <div className="text-base font-medium text-white">{title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{description}</div>
        </div>
        <Button onClick={handle} disabled={busy} size="sm" className="w-full"
                 data-testid={`${testId}-btn`}>
          {busy ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
          Download CSV
        </Button>
      </CardContent>
    </Card>
  );
};

const AdminBackupCentre = () => {
  const [filters, setFilters] = useState({
    tenant_id: '',
    academic_year_id: '',
    class_id: '',
    term_id: '',
    status: '',
    start_date: '',
    end_date: '',
  });
  const setF = (k) => (e) => setFilters({ ...filters, [k]: e.target?.value ?? e });

  const { data: manifest, refetch, isFetching } = useQuery({
    queryKey: ['backup-manifest', filters.tenant_id],
    queryFn: async () => {
      const qs = filters.tenant_id ? `?tenant_id=${encodeURIComponent(filters.tenant_id)}` : '';
      const { data } = await axios.get(`${API}/api/school/backup/manifest${qs}`,
        { headers: authH() });
      return data;
    },
  });

  // Filters shared by all endpoints
  const sharedFilters = {
    tenant_id: filters.tenant_id,
    start_date: filters.start_date,
    end_date: filters.end_date,
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <header>
          <h1 className="text-2xl font-semibold text-white inline-flex items-center gap-2">
            <Database className="h-6 w-6 text-indigo-400" /> Backup Centre
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Download CSV backups of exam results, attendance, library borrows and fee payments.
            All filters are optional — leave blank to export everything.
          </p>
        </header>

        {/* Filters */}
        <Card className="bg-slate-900/60 border-slate-800" data-testid="backup-filters">
          <CardHeader>
            <CardTitle className="text-base text-slate-200">Filters</CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Tenant ID</label>
              <Input value={filters.tenant_id} onChange={setF('tenant_id')}
                     placeholder="(super-admin: blank = all)"
                     className="bg-slate-900 border-slate-800"
                     data-testid="filter-tenant" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Academic year ID</label>
              <Input value={filters.academic_year_id} onChange={setF('academic_year_id')}
                     placeholder="(exams only)"
                     className="bg-slate-900 border-slate-800"
                     data-testid="filter-year" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Class ID</label>
              <Input value={filters.class_id} onChange={setF('class_id')}
                     placeholder="(exams + attendance)"
                     className="bg-slate-900 border-slate-800"
                     data-testid="filter-class" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Term ID</label>
              <Input value={filters.term_id} onChange={setF('term_id')}
                     placeholder="(fees only)"
                     className="bg-slate-900 border-slate-800"
                     data-testid="filter-term" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Library status</label>
              <Input value={filters.status} onChange={setF('status')}
                     placeholder="active | returned | overdue | damaged"
                     className="bg-slate-900 border-slate-800"
                     data-testid="filter-status" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Start date</label>
              <Input type="date" value={filters.start_date} onChange={setF('start_date')}
                     className="bg-slate-900 border-slate-800"
                     data-testid="filter-start" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">End date</label>
              <Input type="date" value={filters.end_date} onChange={setF('end_date')}
                     className="bg-slate-900 border-slate-800"
                     data-testid="filter-end" />
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => refetch()} disabled={isFetching}
                       className="w-full" data-testid="refresh-counts">
                <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
                Refresh counts
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Export tiles */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <ExportTile
            icon={GraduationCap} accent="indigo"
            title="Exam marks"
            description="All recorded marks, grades, GPA & remarks per student/subject."
            count={manifest?.exam_marks}
            endpoint="/api/school/backup/exam-marks.csv"
            fallbackName="exam-marks.csv"
            sharedFilters={sharedFilters}
            extraFilters={{
              academic_year_id: filters.academic_year_id,
              class_id: filters.class_id,
            }}
            testId="export-exam-marks"
          />
          <ExportTile
            icon={Calendar} accent="emerald"
            title="Attendance"
            description="Daily attendance records (present/absent/late) with source."
            count={manifest?.attendance}
            endpoint="/api/school/backup/attendance.csv"
            fallbackName="attendance.csv"
            sharedFilters={sharedFilters}
            extraFilters={{ class_id: filters.class_id }}
            testId="export-attendance"
          />
          <ExportTile
            icon={BookOpen} accent="amber"
            title="Library borrows"
            description="Borrowed / returned / overdue book transactions."
            count={manifest?.library_borrows}
            endpoint="/api/school/backup/library-borrows.csv"
            fallbackName="library-borrows.csv"
            sharedFilters={sharedFilters}
            extraFilters={{ status: filters.status }}
            testId="export-library-borrows"
          />
          <ExportTile
            icon={Receipt} accent="sky"
            title="Fee payments"
            description="Receipts, methods, amounts, references — for accounting backup."
            count={manifest?.fee_payments}
            endpoint="/api/school/backup/fee-payments.csv"
            fallbackName="fee-payments.csv"
            sharedFilters={sharedFilters}
            extraFilters={{ term_id: filters.term_id }}
            testId="export-fee-payments"
          />
        </div>

        <Card className="bg-slate-900/30 border-slate-800/50">
          <CardContent className="p-4 text-xs text-slate-500">
            Tip: empty filters export everything across the platform. Add a tenant
            ID to scope a single school. CSV files are timestamped and stream
            directly from MongoDB — even very large exports start instantly.
          </CardContent>
        </Card>

        <SchoolBackupSchedulePanel tenantId={filters.tenant_id} />
      </div>
    </div>
  );
};

export default AdminBackupCentre;
